import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Container, Typography, Card, CardContent, 
  Box, IconButton, CircularProgress, Select, MenuItem, SelectChangeEvent, Fab
} from '@mui/material';
import { getStops, editStops, deleteSTMSStops } from './action.ts';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import Heading from '../../Components/heading.tsx';
import ConfirmationPopup from '../../../WebApp/Components/ConfirmationModal/confirmation-modal.tsx';
import EditStopNameModal from './edit-stops-modal.tsx';
import AddStopsModal from './add-stops-modal.tsx'; 
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FooterLogo from '../../Components/footer.tsx';

const styles = {
  container: { display: 'flex', flexDirection: 'column' as const, height: '100vh' },
  header: { marginTop: '30px', marginBottom: '0px' },
  mapContainer: { flex: 1, borderRadius: '8px', overflow: 'hidden', width: '100%', minHeight: '600px', position: 'relative' as const },
  loaderContainer: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }
};

interface Stop {
  id: string;
  name: string;
  status: string;
  time?: string;
  center_lat?: number;
  center_lng?: number;
  original_address?: string;
}

const StopsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get('vehicle_id') || '';
  const userId = searchParams.get('user_id') || '';
  const token = searchParams.get('token') || '';
  const userName = searchParams.get('user_name') || '';
  const companiesId = searchParams.get('companies_id') || '';
  const vehicleNumber = searchParams.get('vehicle_number') || '';
  const [stopsData, setStopsData] = useState<Stop[]>([]);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState<boolean>(false);
  const [pendingStatus, setPendingStatus] = useState<{ stopId: string, newStatus: string } | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingStop, setEditingStop] = useState<Stop | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const observerTarget = useRef<HTMLDivElement>(null);
  const formatAddress = (fullAddress: string) => {
    if (!fullAddress) return 'Unknown Stop';
    const parts = fullAddress.split(',').map(p => p.trim());
    if (parts.length < 3) return fullAddress;

    const selectedParts = parts.slice(1, 3).filter(
      p => !p.toLowerCase().includes('india') && !/\b\d{6}\b/.test(p)
    );
    return selectedParts.join(', ') || parts[0];
  };

  const fetchStopsData = useCallback((pageNum: number, isReset: boolean = false) => {
    if (!vehicleId) return;

    setLoading(true);
    getStops(
      vehicleId,
      10,
      pageNum,
      (res: any) => {
        const dataList = res?.data || res || [];
        const formattedStops = dataList.map((stop: any) => ({
          id: stop.id,
          name: formatAddress(stop.name || stop.stop_name),
          status: stop.status || 'pending',
          time: stop.time || stop.eta || stop.distance || 'Time N/A',
          center_lat: stop.center_lat,
          center_lng: stop.center_lng,
          original_address: stop.name || stop.stop_name
        }));

        setStopsData(prev => isReset ? formattedStops : [...prev, ...formattedStops]);

        const totalPages = res?.total_pages;
        if (totalPages !== undefined) {
            setHasMore(pageNum + 1 < totalPages);
        } else {
            setHasMore(dataList.length === 10);
        }

        setLoading(false);
      },
      (err: any) => {
        console.error("Failed to fetch stops", err);
        toast.error("Failed to load stops list.");
        setLoading(false);
      }
    );
  }, [vehicleId]);

  useEffect(() => {
    setPage(0);
    fetchStopsData(0, true);
  }, [vehicleId, fetchStopsData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage((prevPage) => {
            const nextPage = prevPage + 1;
            fetchStopsData(nextPage, false);
            return nextPage;
          });
        }
      },
      { threshold: 1.0 }
    );

    if (observerTarget.current) {
      observer.observe(observerTarget.current);
    }

    return () => {
      if (observerTarget.current) {
        observer.unobserve(observerTarget.current);
      }
    };
  }, [hasMore, loading, fetchStopsData]);

  const handleBack = () => {
    navigate(`/wv/dashboard?user_name=${userName}&vehicle_id=${vehicleId}&user_id=${userId}&token=${token}`);
  };

  const handleStatusChangeClick = (stopId: string, event: SelectChangeEvent<string>) => {
    const newStatus = event.target.value;
    setPendingStatus({ stopId, newStatus });
    setIsConfirmOpen(true);
  };

  const handleConfirmStatusChange = () => {
    if (!pendingStatus) return;
    const { stopId, newStatus } = pendingStatus;
    
    const handleSuccess = (res: any) => {
      toast.success(newStatus === 'deleted' ? 'Stop deleted successfully' : `Status updated to ${newStatus}`);
      setIsConfirmOpen(false);
      setPendingStatus(null);
      setPage(0);
      fetchStopsData(0, true);
    };

    const handleError = (err: any) => {
      toast.error(err?.message || (newStatus === 'deleted' ? 'Failed to delete stop.' : 'Failed to update stop status.'));
      setIsConfirmOpen(false);
      setPendingStatus(null);
    };

    if (newStatus === 'deleted') {
      deleteSTMSStops(
        stopId,
        handleSuccess,
        handleError,
        companiesId
      );
    } else {
      editStops(
        newStatus,
        stopsData.find((stop: { id: any; }) => stop.id === stopId)?.name || null,
        stopId,
        vehicleId,
        stopsData.find(stop => stop.id === stopId)?.center_lat ? String(stopsData.find(stop => stop.id === stopId)?.center_lat) : null,
        stopsData.find(stop => stop.id === stopId)?.center_lng ? String(stopsData.find(stop => stop.id === stopId)?.center_lng) : null,
        handleSuccess,
        handleError
      );
    }
  };

  const handleCancelStatusChange = () => {
    setIsConfirmOpen(false);
    setPendingStatus(null);
  };

  const handleMapClick = (lat?: number, lng?: number, address?: string) => {
    if (lat && lng) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`, '_blank');
    } else if (address) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`, '_blank');
    } else {
      toast.error("Location details not available");
    }
  };

  const handleOpenEdit = (stop: Stop) => {
    setEditingStop(stop);
    setIsEditModalOpen(true);
  };

  const handleCloseEdit = () => {
    setIsEditModalOpen(false);
    setEditingStop(null);
  };

  const handleSaveName = (newName: string, newLat?: string, newLng?: string) => {
    if (!editingStop) return;

    const handleSuccess = (res: any) => {
      toast.success('Stop updated successfully');
      handleCloseEdit();
      setPage(0);
      fetchStopsData(0, true);
    };

    const handleError = (err: any) => {
      toast.error(err?.message || 'Failed to update stop.');
      handleCloseEdit();
    };
    editStops(
      editingStop.status,
      newName,
      editingStop.id,
      vehicleId,
      newLat || '',
      newLng || '',
      handleSuccess,
      handleError
    );
  };

  const handleAddStop = () => {
    if (!vehicleId) {
      toast.error("Vehicle ID is missing");
      return;
    }
    setIsAddModalOpen(true);
  };

  const handleAddStopSuccess = () => {
    setPage(0);
    fetchStopsData(0, true);
  };

  return (
    <Box 
      sx={{ 
        background: 'linear-gradient(45deg, black, rgb(237, 27, 27))',
        minHeight: '100vh',
        pt: 0,
        pb: 4,
        position: 'relative' 
      }}
    >
      <Container maxWidth="xs" sx={{ padding: 0 }}>

        <Box style={styles.header}>
          <Heading 
            title="List of Stops" 
            onBackClick={handleBack} 
            vehicle={vehicleNumber}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', p: 1 }}>
          {stopsData.length === 0 && !loading ? (
            <Typography variant="body1" align="center" color="white" sx={{ mt: 4 }}>
              No stops found.
            </Typography>
          ) : (
            stopsData.map((stop, index) => {
              const currentStatus = stop.status.toLowerCase();

              return (
                <Card 
                  key={`${stop.id}-${index}`} 
                  sx={{ 
                    borderRadius: '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    border: '1px solid #e0e0e0',
                    overflow: 'visible',
                    bgcolor: '#ffffff'
                  }}
                >
                  <CardContent 
                    sx={{ 
                      pb: '12px !important', 
                      paddingTop: '12px !important', 
                    }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Box display="flex" alignItems="center" gap={1.5} flex={1} overflow="hidden">
                        <IconButton 
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMapClick(stop.center_lat, stop.center_lng, stop.original_address);
                          }}
                          sx={{ 
                            bgcolor: 'rgba(237, 27, 27, 0.1)', 
                            color: 'rgb(237, 27, 27)', 
                            width: 40, 
                            height: 40,
                            borderRadius: '50%',
                            flexShrink: 0,
                            '&:hover': { bgcolor: 'rgba(237, 27, 27, 0.2)' }
                          }}
                        >
                          <LocationOnIcon />
                        </IconButton>

                        <Typography 
                          variant="subtitle1" 
                          fontWeight="bold" 
                          sx={{ 
                            whiteSpace: 'nowrap', 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis',
                            color: 'black',
                            lineHeight: 1.2,
                            fontSize: '0.9rem',
                            flexGrow: 1, 
                            pr: 1 
                          }}
                        >
                          {stop.name}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1} ml={1}>
                        {(currentStatus === 'pending' || currentStatus === 'approved') && (
                          <Select
                            size="small"
                            value={currentStatus}
                            onChange={(e) => handleStatusChangeClick(stop.id, e)} 
                            renderValue={(selected) => {
                              if (selected === 'approved') return 'Approved';
                              if (selected === 'pending') return 'Pending';
                              return selected;
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              height: '32px',
                              fontWeight: 'bold',
                              color: currentStatus === 'approved' ? 'green' : 'black',
                              backgroundColor: currentStatus === 'approved' ? '#e8f5e9' : '#fff9c4',
                              '.MuiSelect-select': { 
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                paddingRight: '24px' 
                              },
                              fieldset: { border: 'none' },
                              '& .MuiSelect-icon': {
                                color: currentStatus === 'approved' ? 'green' : 'inherit'
                              }
                            }}
                          >
                            {currentStatus === 'pending' && <MenuItem value="pending" sx={{ display: 'none' }}>Pending</MenuItem>}
                            {currentStatus === 'pending' && <MenuItem value="approved" sx={{ fontSize: '0.8rem' }}>Approve</MenuItem>}
                            {currentStatus === 'pending' && <MenuItem value="rejected" sx={{ fontSize: '0.8rem' }}>Reject</MenuItem>}
                            {currentStatus === 'approved' && <MenuItem value="approved" sx={{ display: 'none' }}>Approved</MenuItem>}
                            {currentStatus === 'approved' && <MenuItem value="deleted" sx={{ fontSize: '0.8rem', color: 'red' }}>Delete</MenuItem>}
                          </Select>
                        )}

                        {currentStatus === 'rejected' && (
                          <Typography 
                            sx={{ 
                              color: 'red', fontWeight: 'bold', fontSize: '0.75rem', 
                              bgcolor: '#ffebee', px: 1, py: 0.5, borderRadius: 1,
                              display: 'flex', alignItems: 'center', height: '32px', boxSizing: 'border-box'
                            }}
                          >
                            Rejected
                          </Typography>
                        )}

                        <IconButton 
                          onClick={() => handleOpenEdit(stop)} 
                          size="small" 
                          sx={{ color: 'grey.600', padding: '4px', flexShrink: 0 }}
                        >
                          <EditIcon fontSize="small" sx={{ width: '18px', height: '18px' }} />
                        </IconButton>
                      </Box>

                    </Box>
                  </CardContent>
                </Card>
              );
            })
          )}
        </Box>

        <div ref={observerTarget} style={{ height: '40px', display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
          {loading && <CircularProgress size={24} sx={{ color: 'white' }} />}
        </div>

      </Container>

      <Fab 
        aria-label="add" 
        onClick={handleAddStop}
        sx={{
          position: 'fixed',
          bottom: 70,
          right: 20,
          backgroundColor: '#ffffff',
          color: 'rgb(237, 27, 27)',
          '&:hover': {
            backgroundColor: '#f5f5f5',
          },
          boxShadow: '0px 4px 12px rgba(0,0,0,0.3)',
          zIndex: 1000
        }}
      >
        <AddIcon />
      </Fab>

   <ConfirmationPopup
        open={isConfirmOpen}
        onClose={handleCancelStatusChange}
        onConfirm={handleConfirmStatusChange}
        title={pendingStatus?.newStatus === 'deleted' ? "Confirm Delete" : "Confirm Status Change"}
        message={
          pendingStatus?.newStatus === 'deleted'
            ? "Are you sure you want to delete this stop?"
            : `Are you sure you want to change the status to ${pendingStatus?.newStatus}?`
        }
      />
     <EditStopNameModal
        open={isEditModalOpen}
        initialName={editingStop?.name || ''}
        initialLat={editingStop?.center_lat}
        initialLng={editingStop?.center_lng}
        status={editingStop?.status || 'pending'}
        onClose={handleCloseEdit}
        onSave={handleSaveName}
      />

      <AddStopsModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleAddStopSuccess}
        vehicleId={vehicleId}
        companiesId={companiesId}
      />

      <FooterLogo />
    </Box>
  );
};

export default StopsPage;